import { IcPlus, IcTrash } from '@assets/icons/24x24';
import { createListCollection, For, HStack, Icon, IconButton, Input, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';

import { CustomSelect } from './select/Select';

type Props<T extends string> = {
  items: [string, T][];
  enumList: T[];
  onChange: (items: [string, T][]) => void;
  isRequired?: boolean;
  formatLabel?: (value: string) => string;
};

export default function TupleList<T extends string>({
  items,
  formatLabel = (value) => value,
  enumList,
  onChange,
  isRequired = false,
}: Readonly<Props<T>>) {
  const collection = useMemo(
    () =>
      createListCollection({
        items: enumList.map((item) => ({
          label: formatLabel(item),
          value: item,
        })),
      }),
    [enumList, formatLabel],
  );

  const handleInputChange = (index: number, newLabel: string) => {
    const newItems = [...items];
    newItems[index] = [newLabel, newItems[index][1]];
    onChange(newItems);
  };

  const handleSelectChange = (index: number, newValue: string) => {
    const newItems = [...items];
    newItems[index] = [newItems[index][0], newValue as T];
    onChange(newItems);
  };

  const handleDelete = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const handleAdd = () => {
    if (enumList.length === 0) {
      return;
    }
    const newItems: [string, T][] = [...items, ['', enumList[0]]];
    onChange(newItems);
  };

  return (
    <VStack width="100%" gap={2} alignItems="start">
      <For each={items}>
        {(item, index) => (
          <HStack width="100%" gap={2} key={index}>
            <Input
              name={`chip-value-${index}`}
              id={`chip-value-${index}`}
              value={item[0]}
              onChange={(e) => handleInputChange(index, e.target.value)}
            />
            <CustomSelect
              value={item[1]}
              collection={collection}
              size="sm"
              onChange={(value) => value && handleSelectChange(index, value)}
            />
            {(!isRequired || items.length > 1) && (
              <IconButton variant="pick" aria-label="delete" onClick={() => handleDelete(index)}>
                <Icon as={IcTrash} boxSize="24px" />
              </IconButton>
            )}
          </HStack>
        )}
      </For>
      <IconButton variant="pick" aria-label="add" onClick={handleAdd}>
        <Icon as={IcPlus} boxSize="24px" />
      </IconButton>
    </VStack>
  );
}
